import { Stack, Typography, useTheme } from "@mui/material";
import { ChevronRight } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { useFormatDateAndTimes } from "web_myplann/Activities/Hooks/useActivitiesFormatDateAndTimes";
import type { ActivityListItemResponseDTO } from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { ActivityIcon } from "web_myplann/Shared/Icons";
import { CIRCLE_SIGN, LIST_ICON_SIZE } from "web_myplann/Utils/constants";

type Props = {
	data: ActivityListItemResponseDTO;
	hasMoreConnectedAgents: boolean;
};

export const ActivityItem: FC<Props> = ({ data, hasMoreConnectedAgents }) => {
	const { t } = useResource();
	const theme = useTheme();
	const navigate = useNavigate();
	const formatDateAndTimes = useFormatDateAndTimes();

	const entityId = String(data.id ?? "");
	const title = data.title;
	const date = data.date ?? "";
	const timeFrom = data.timeFrom ?? null;
	const timeTo = data.timeTo ?? null;
	const agentPersonName = data.author.name;
	const place = data.place ?? undefined;

	const enhancedTitle = title ? `${CIRCLE_SIGN} ${title}` : "";

	const enhancedType = t(Resources.Activity.List.ActivityType[data.type]);

	const navigateToEvent = (id: string) => {
		if (data.id != null) {
			navigate(AppRouting.MyPlann.ActivityDetail.path(id));
		}
	};

	return (
		<Stack
			direction={"row"}
			justifyContent={"space-between"}
			alignItems={"center"}
			onClick={() => navigateToEvent(entityId)}
			sx={{ cursor: "pointer" }}
		>
			<Stack
				direction={"row"}
				gap={1.2}
				alignItems={"center"}
				flex={1}
				minWidth={0}
			>
				<Stack
					width={32}
					height={32}
					alignItems="center"
					justifyContent="center"
				>
					<ActivityIcon width={LIST_ICON_SIZE} height={LIST_ICON_SIZE} />
				</Stack>
				<Stack direction={"column"} flex={1} minWidth={0}>
					<Typography
						variant="deprecatedCaption"
						color={theme.palette.deprecated.grey[500]}
					>
						{formatDateAndTimes(date, timeFrom, timeTo)}
					</Typography>

					<Typography
						variant="deprecatedBodyStrong"
						color={theme.palette.deprecated.grey[700]}
						whiteSpace="pre-line"
						display="-webkit-box"
						overflow="hidden"
						sx={{
							wordBreak: "normal",
							overflowWrap: "break-word",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 2,
						}}
					>
						{enhancedType} {enhancedTitle}
					</Typography>

					<Typography
						variant="deprecatedCaption"
						color={theme.palette.deprecated.grey[500]}
						whiteSpace="pre-line"
						display="-webkit-box"
						overflow="hidden"
						sx={{
							wordBreak: "break-all",
							WebkitBoxOrient: "vertical",
							WebkitLineClamp: 1,
						}}
					>
						{place ?? ""}
					</Typography>

					{hasMoreConnectedAgents && (
						<Typography
							variant="deprecatedCaption"
							color={theme.palette.deprecated.grey[500]}
							whiteSpace="pre-line"
							display="-webkit-box"
							overflow="hidden"
							sx={{
								wordBreak: "break-all",
								WebkitBoxOrient: "vertical",
								WebkitLineClamp: 1,
							}}
						>
							{agentPersonName} (
							{t(Resources.Dashboard.UserType[data.author.type])})
						</Typography>
					)}
				</Stack>
			</Stack>

			<Stack pl={1.2}>
				<ChevronRight
					size={theme.iconSize.sm}
					strokeWidth={1.5}
					cursor={"pointer"}
					color={theme.palette.primary.main}
				/>
			</Stack>
		</Stack>
	);
};
